import {
    buildCreateApi,
    coreModule,
    reactHooksModule,
    type FetchArgs,
    type BaseQueryApi
} from '@reduxjs/toolkit/query/react';
import { Mutex } from 'async-mutex';
import { FetchBaseQueryArgs } from '@reduxjs/toolkit/dist/query/fetchBaseQuery';

import { normalizeUrl } from '#helper/normalize-url';
import type { TStore } from '#core/store';
import { api as apiAuth } from '#adapter/api/auth';
import { enhancedBaseQuery as enhancedBaseQueryFn } from '#adapter/enhanced-base-query';

import { slice as sliceUser } from '#component/user/slice';

import { environment } from '#environment';

/*
    Если у вас есть необходимость в использование контекста хранилища для createApi.
    Вы это можете сделать здесь по нижеприведенному примеру.

    https://redux-toolkit.js.org/rtk-query/usage/customizing-create-api

    Example:

    import { createDispatchHook, createSelectorHook, createStoreHook } from 'react-redux';

    export const createApi = buildCreateApi(
        coreModule(),
        reactHooksModule({
            useDispatch: createDispatchHook(context),
            useSelector: createSelectorHook(context),
            useStore   : createStoreHook(context)
        })
    );
*/

export const createApi = buildCreateApi(coreModule(), reactHooksModule());

const optionsFetchBaseQuery: FetchBaseQueryArgs = {
    baseUrl       : normalizeUrl(`${environment.sdkApiUrl}/`),
    headers       : { 'cache-control': 'no-cache' },
    prepareHeaders: (headers, api) => {
        const { supplierOldID } = (api.getState() as TStore)[sliceUser.name];
        const token = localStorage.getItem(String(import.meta.env.APP_ACCESS_TOKEN_KEY));

        supplierOldID && headers.append('X-SupplierId', supplierOldID.toString());
        token && headers.append('Authorizev3', token);

        return headers;
    }
};

const mutex = new Mutex();

/*
    Подробное описание допустимых параметров и примеров `fetchBaseQuery` можно посмотреть по ссылке.

    https://redux-toolkit.js.org/rtk-query/api/fetchBaseQuery#parameters
*/

export const customBaseQuery = async (args: string | FetchArgs, api: BaseQueryApi, extraOptions: Record<PropertyKey, unknown>) => {
    await mutex.waitForUnlock();

    const enhancedBaseQuery = enhancedBaseQueryFn(optionsFetchBaseQuery);

    let result = await enhancedBaseQuery(args, api, extraOptions);

    if(result.error?.status === 401) {
        localStorage.removeItem(String(import.meta.env.APP_ACCESS_TOKEN_KEY));

        if(!mutex.isLocked()) {
            const release = await mutex.acquire();

            try {
                await api.dispatch(apiAuth.endpoints.check.initiate()).unwrap();

                result = await enhancedBaseQuery(args, api, extraOptions);
            } finally {
                release();
            }
        } else {
            await mutex.waitForUnlock();

            result = await enhancedBaseQuery(args, api, extraOptions);
        }
    }

    return result;
};

export const separateApiPortalGate = createApi({
    reducerPath: '/portal-gate',
    baseQuery  : customBaseQuery,
    endpoints  : () => ({})
});
